import {Component, computed, OnInit, Signal} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {combineLatest, of} from 'rxjs';
import {Apollo} from 'apollo-angular';
import {CombinedGraphQLErrors} from '@apollo/client/errors';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  skipWhile,
  switchMap,
  tap
} from 'rxjs/operators';
import {
  Airline,
  Mutation as WorkflowMutation,
  PoItem,
  PoItemPreview,
  ProcessStateEnum,
  Query
} from '../../../types-workflow';
import gql from 'graphql-tag';
import {PUBLISH_PO_ITEM} from '../../../queries-workflow';
import {BreadCrumb, LogoAreaComponent} from '../../logo-area/logo-area.component';
import {HttpClient} from '@angular/common/http';
import {Organization} from '../../../types-repository';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import {ClTabsModule} from '../../cl-tabs/cl-tabs.module';

import {PlayerComponent} from '../player/player.component';
import {VideoLadderComponent} from '../video-ladder/video-ladder.component';
import {
  FlytedgeMetadataPreviewComponent
} from '../../ifes/flytedge-metadata-preview/flytedge-metadata-preview.component';
import {FlytedgeMetadataRawComponent} from '../../ifes/flytedge-metadata-raw/flytedge-metadata-raw.component';
import {ContextService} from '../../../services/context.service';
import {Dialog} from '@angular/cdk/dialog';
import {ProcessService} from '../../../services/process.service';
import {BannerService} from '../../../banner/banner.service';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {PO_DESTINATIONS} from '../../../target_systems';
import {toSignal} from '@angular/core/rxjs-interop';


const PREVIEW_QUERY = gql`query PreviewQuery($po_name: String!, $po_item_id: String!) {
  list_POs(input: { filter: { po_name: { eq: $po_name}}}) {
    pos {
      id
      po_name
      airline
      poitems(po_item_id: $po_item_id) {
        id
        po_item_id
        filename
        po_destination
        po {
          po_name
          airline
        }
        publish_process {
          id
          state
          message
          start_date
          end_date
        }
        preview {
          dash_manifest_last_modified
          dash_manifest_url
          hls_manifest_url
          hls_manifest_last_modified
          drmtoday_token
          access_token
          ab_token
        }
      }
    }
  }
}
`

@Component({
  selector: 'app-preview',
  templateUrl: './preview-poitem.component.html',
  styleUrls: ['./preview-poitem.component.css'],
  imports: [
    HeadlineSectionComponent,
    LogoAreaComponent,
    ClTabsModule,
    PlayerComponent,
    VideoLadderComponent,
    FlytedgeMetadataPreviewComponent,
    FlytedgeMetadataRawComponent,
    RouterLink
]
})
export class PreviewPoitemComponent implements OnInit {

  poItem: Signal<PoItem | undefined>;
  preview: Signal<PoItemPreview | undefined>;
  po_item_id: Signal<string | undefined>;
  filename: Signal<string | undefined>;
  flytedgeMetadataUrl: Signal<string | null | undefined>;
  breadcrumbs: Signal<BreadCrumb[]>;

  airline: Signal<Airline | null>;
  organization: Signal<Organization | null>;

  protected readonly PO_DESTINATIONS = PO_DESTINATIONS;
  publish_single_po_inflight = false;

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
    contextService: ContextService,
    private dialog: Dialog,
    private processService: ProcessService,
    private banner: BannerService,
    private http: HttpClient,
  ) {

    this.airline = contextService.airline;
    this.organization = contextService.organization;

    // Get route params as observables
    const po_name$ = this.route.params.pipe(
      map(p => p['po']),
      distinctUntilChanged()
    );

    const po_item_id$ = this.route.params.pipe(
      map(p => p['poitem']),
      distinctUntilChanged()
    );

    // Query for PO item data
    const poItemData$ = combineLatest([po_name$, po_item_id$]).pipe(
      mergeMap(([po_name, po_item_id]) => {
        return this.apollo.use('workflow').watchQuery<Query>({
          query: PREVIEW_QUERY,
          errorPolicy: 'all',
          variables: { po_name, po_item_id }
        }).valueChanges;
      }),
      tap(result => {
        if (CombinedGraphQLErrors.is(result.error)) {
          const previewError = result.error.errors.find(e => e.path?.includes('preview'));
          if (previewError) {
            this.banner.open(`Preview unavailable: ${previewError.message}`, ['Dismiss']);
          }
        }
      }),
      filter(result => !!result.data?.list_POs?.pos?.[0]),
      map(result => result.data.list_POs.pos[0].poitems[0] as PoItem),
      shareReplay(1)
    );

    // Convert to signals
    this.poItem = toSignal(poItemData$);

    this.preview = computed(() => {
      const poItem = this.poItem();
      return poItem?.preview;
    });

    this.po_item_id = computed(() => {
      const poItem = this.poItem();
      return poItem?.po_item_id;
    });

    this.filename = computed(() => {
      const poItem = this.poItem();
      return poItem?.filename;
    });

    // Flytedge metadata URL - needs to stay as observable for HTTP call, then convert to signal
    const flytedgeMetadataUrl$ = poItemData$.pipe(
      filter(poItem => poItem?.preview != null),
      map(poItem => {
        const preview = poItem.preview;
        let filename = poItem.filename;
        const url = preview.dash_manifest_url != null ? preview.dash_manifest_url : preview.hls_manifest_url;
        if (filename.includes('/')) {
          const filename_parts = filename.split('/');
          filename = filename_parts[filename_parts.length - 1];
        }
        return url.split('/').slice(0, -1).join('/') + '/feed/' + filename + '.json';
      }),
      mergeMap(url => this.http.get<any>(url).pipe(
        catchError(() => of(null)),
        map((res: any) => res ? url : null)
      )),
      shareReplay(1)
    );

    this.flytedgeMetadataUrl = toSignal(flytedgeMetadataUrl$);
  }

  ngOnInit() {
    // Create breadcrumbs as a computed signal
    this.breadcrumbs = computed(() => {
      const poItem = this.poItem();
      const org = this.organization();
      const airline = this.airline();

      if (!poItem || !org || !airline) {
        return [];
      }

      const po_name = poItem.po.po_name;

      return [
        {
          display: 'Content Platform',
          commands: ['/o', org.id],
        },
        {
          display: 'Workflows',
          commands: ['/o', org.id, 'workflows'],
        },
        {
          display: airline.airline_name,
          commands: ['/o', org.id, 'workflows', airline.iata_code],
        },
        {
          display: po_name,
          commands: ['/o', org.id, 'workflows', airline.iata_code, po_name],
        }
      ];
    });
  }

  publish_single_po(poItem: PoItem) {
    if (!poItem) {
      return;
    }

    const action = () => {
      this.publish_single_po_inflight = true;
      this.apollo.use('workflow').mutate<WorkflowMutation>({
        mutation: PUBLISH_PO_ITEM,
        variables: {
          po_name: poItem.po.po_name,
          po_item_id: poItem.po_item_id,
        }
      }).pipe(
        tap((res) => {
          this.publish_single_po_inflight = false;
          const client = this.apollo.use('workflow').client;
          const cache = client.cache;
          cache.modify({
            id: cache.identify({__typename: 'POItem', id: poItem.id}),
            fields: {
              publish_process(existingPublishProcess, {toReference}) {
                return toReference(res.data.publishPoItem);
              },
            },
          });
        }),
        switchMap(d => this.processService.observeProcess(d.data.publishPoItem, {
          successMessage: `${poItem.po_item_id} published successfully.`,
          failureMessage: `${poItem.po_item_id} failed to publish.`,
          link: window.location.href
        })),
        skipWhile(process => process.state === ProcessStateEnum.InProgress)
      ).subscribe({
        error: (error) => {
          this.banner.open(error, ['Close']);
        }
      });
    };

    if (poItem.publish_process &&
      (poItem.publish_process.state === ProcessStateEnum.InProgress ||
        poItem.publish_process.state === ProcessStateEnum.Success)) {
      let secondLine = '';
      if (poItem.publish_process.state === ProcessStateEnum.InProgress) {
        secondLine = 'This will restart an already running publish process.';
      }
      if (poItem.publish_process.state === ProcessStateEnum.Success) {
        secondLine = 'This will restart a completed publish process.';
      }
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          headline: 'Publish',
          message: `Do you really want to publish ${poItem.po_item_id}?<br/>${secondLine}`,
          actionName: 'Publish',
          action: action
        }
      });
    } else {
      action();
    }
  }
}
